/**
 * TableRenderer
 * 
 * Specialized renderer for tables using jspdf-autotable.
 * Provides clean API for rendering specification tables, inspection tables, etc.
 */

import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { CONTROL_CARD_CONFIG, CONTROL_CARD_TEXT } from '../../config'

// Extend jsPDF type to include lastAutoTable property
declare module 'jspdf' {
  interface jsPDF {
    lastAutoTable: {
      finalY: number
      table: any
    }
  }
}

/**
 * Table configuration options
 */
export interface TableConfig {
  startY: number
  startX?: number
  tableWidth?: number
  columnWidths?: number[]
  columnRatios?: number[]
  headerStyle?: {
    fillColor?: [number, number, number]
    textColor?: [number, number, number]
    fontSize?: number
    fontStyle?: 'normal' | 'bold'
    cellPadding?: number
  }
  bodyStyle?: {
    fontSize?: number
    cellPadding?: number
    textColor?: [number, number, number]
    halign?: 'left' | 'center' | 'right'
  }
  borderWidth?: number
  borderColor?: [number, number, number]
}

/**
 * Configuration for page header on new pages
 */
export interface PageHeaderConfig {
  reportNumber: string
  ownerName: string
  serviceOrder: string | null | undefined
}

/**
 * Async callback for rendering full header on continuation pages
 */
export type ContinuationHeaderCallback = (doc: jsPDF) => Promise<number>

/**
 * Renderer for PDF tables
 */
export class TableRenderer {
  private readonly config: typeof CONTROL_CARD_CONFIG
  
  constructor(config: typeof CONTROL_CARD_CONFIG = CONTROL_CARD_CONFIG) {
    this.config = config
  }

  /**
   * Render a specification table with 3-column layout
   * Each cell contains "Label\nValue" format
   * Now async to properly handle continuation header rendering with logos
   */
  async renderSpecificationTable(
    doc: jsPDF,
    equipmentHeader: string | null,
    tableData: string[][],
    startY: number,
    startX?: number,
    pageHeader?: PageHeaderConfig,
    continuationHeaderCallback?: ContinuationHeaderCallback
  ): Promise<number> {
    const tableStartX = startX ?? this.config.layout.tableStartX
    const tableWidth = this.config.layout.tableWidth
    let currentY = startY
    
    // Track segment start for borders
    let segmentStartY = startY

    // Pagination constants
    const pageHeight = (doc as any).internal.pageSize.height || (doc as any).internal.pageSize.getHeight()
    const marginBottom = this.config.layout.margin.bottom
    const safeBottomY = pageHeight - marginBottom

    // Helper to draw vertical borders
    const drawVerticalBorders = (y1: number, y2: number) => {
      doc.setLineWidth(this.config.borders.standard)
      doc.setDrawColor(...this.config.colors.border)
      doc.line(tableStartX, y1, tableStartX, y2)
      doc.line(tableStartX + tableWidth, y1, tableStartX + tableWidth, y2)
    }

    // Helper to handle pagination - now async
    const handlePageBreak = async () => {
       // Draw borders for the current segment
       drawVerticalBorders(segmentStartY, currentY)
       // Draw bottom border for current page
       doc.line(tableStartX, currentY, tableStartX + tableWidth, currentY)
       
       doc.addPage()
       
       // Reset Y to top margin
       currentY = this.config.layout.margin.top
       
       // If we have a continuation header callback, use it (for full header with logos)
       if (continuationHeaderCallback) {
         currentY = await continuationHeaderCallback(doc)
       } else if (pageHeader) {
         currentY = this.renderCompanyInfoTable(
           doc,
           pageHeader.reportNumber,
           pageHeader.ownerName,
           pageHeader.serviceOrder,
           currentY
         )
       }
       
       segmentStartY = currentY
    }

    // Render equipment header if provided
    if (equipmentHeader) {
      const fontSizeInUnits = this.config.typography.equipmentHeader.size * 0.35
      const headerHeight = this.config.spacing.headerAfter + fontSizeInUnits
      const firstRowHeight = tableData.length > 0 ? this.config.spacing.rowHeight : 0
      
      if (currentY + headerHeight + firstRowHeight > safeBottomY) {
         await handlePageBreak()
      }

      doc.setFont(
        this.config.typography.equipmentHeader.font,
        this.config.typography.equipmentHeader.weight
      )
      doc.setFontSize(this.config.typography.equipmentHeader.size)
      doc.setTextColor(...this.config.colors.text)
      doc.text(equipmentHeader, tableStartX + 2, currentY + 4, { align: 'left' })
      
      currentY += this.config.spacing.headerAfter
    }

    // Render specification rows manually for precise control
    if (tableData.length > 0) {
      const columnCount = tableData[0]?.length || 3
      const columnWidth = tableWidth / columnCount
      const rowHeight = this.config.spacing.rowHeight

      for (const row of tableData) {
        // Check for overflow
        if (currentY + rowHeight > safeBottomY) {
             await handlePageBreak()
        }

        let currentX = tableStartX
        
        for (let i = 0; i < row.length; i++) {
          const cellContent = row[i]
          
          // Only render if cell has content
          if (cellContent && cellContent.trim() !== '') {
            const [label, value] = cellContent.split('\n').map(s => s.trim())
            
            const textX = currentX + 2
            
            // Render label in bold
            doc.setFont(
              this.config.typography.label.font,
              this.config.typography.label.weight
            )
            doc.setFontSize(this.config.typography.label.size)
            doc.setTextColor(...this.config.colors.text)
            doc.text(label, textX, currentY + 3.5)
            
            // Render value in normal font
            doc.setFont(
              this.config.typography.body.font,
              this.config.typography.body.weight
            )
            doc.setFontSize(this.config.typography.body.size)
            doc.setTextColor(...this.config.colors.text)
            doc.text(value || '', textX, currentY + 7.5)
          }
          
          currentX += columnWidth
        }
        
        currentY += rowHeight
      }
    }

    // Draw borders for the final segment
    drawVerticalBorders(segmentStartY, currentY)

    return currentY
  }

  /**
   * Render a standard table using autoTable
   */
  renderTable(
    doc: jsPDF,
    headers: string[][],
    body: string[][],
    tableConfig: TableConfig
  ): number {
    const startX = tableConfig.startX ?? this.config.layout.tableStartX
    const tableWidth = tableConfig.tableWidth ?? this.config.layout.tableWidth

    // Calculate column widths
    const columnStyles: Record<number, any> = {}
    if (tableConfig.columnWidths) {
      tableConfig.columnWidths.forEach((width, index) => {
        columnStyles[index] = { cellWidth: width }
      })
    } else if (tableConfig.columnRatios) {
      tableConfig.columnRatios.forEach((ratio, index) => {
        columnStyles[index] = { cellWidth: tableWidth * ratio }
      })
    }

    autoTable(doc, {
      head: headers,
      body: body,
      startY: tableConfig.startY,
      margin: { left: startX },
      theme: 'grid',
      styles: {
        lineColor: tableConfig.borderColor || this.config.colors.border,
        lineWidth: tableConfig.borderWidth || this.config.borders.thin,
      },
      headStyles: {
        fillColor: tableConfig.headerStyle?.fillColor || this.config.colors.white,
        textColor: tableConfig.headerStyle?.textColor || this.config.colors.text,
        fontStyle: tableConfig.headerStyle?.fontStyle || 'bold',
        fontSize: tableConfig.headerStyle?.fontSize || this.config.typography.tableHeader.size,
        cellPadding: tableConfig.headerStyle?.cellPadding, // Use header-specific padding if provided
      },
      bodyStyles: {
        fontSize: tableConfig.bodyStyle?.fontSize || this.config.typography.body.size,
        cellPadding: tableConfig.bodyStyle?.cellPadding || this.config.spacing.cellPadding,
        textColor: tableConfig.bodyStyle?.textColor || this.config.colors.text,
        halign: tableConfig.bodyStyle?.halign,
      },
      columnStyles,
      willDrawCell: (data) => {
        if (data.cell) {
          data.cell.styles.lineWidth = tableConfig.borderWidth || this.config.borders.thin
        }
      },
    })

    return doc.lastAutoTable.finalY
  }

  /**
   * Render company information table
   * Dynamically shows 2 or 3 columns based on service order presence
   */
  renderCompanyInfoTable(
    doc: jsPDF,
    reportNumber: string,
    ownerName: string,
    serviceOrder: string | null | undefined,
    startY: number
  ): number {
    // Check if service order exists and has a value
    const hasServiceOrder = serviceOrder && serviceOrder.trim().length > 0
    
    // Build headers and body based on service order presence
    const headers = hasServiceOrder
      ? [[CONTROL_CARD_TEXT.no.headers.reportNumber, CONTROL_CARD_TEXT.no.headers.equipmentOwner, 'Service Order']]
      : [[CONTROL_CARD_TEXT.no.headers.reportNumber, CONTROL_CARD_TEXT.no.headers.equipmentOwner]]
    
    const body = hasServiceOrder
      ? [[reportNumber, ownerName, serviceOrder]]
      : [[reportNumber, ownerName]]
    
    // Use appropriate column ratios
    const columnRatios = hasServiceOrder
      ? [1/3, 1/3, 1/3] // 3 equal columns
      : [...this.config.table.columnRatios.reportOwner] // 2 equal columns (0.5, 0.5)
    
    return this.renderTable(
      doc,
      headers,
      body,
      {
        startY,
        columnRatios,
        headerStyle: {
          fontSize: this.config.typography.tableHeader.size,
          cellPadding: 1,
        },
        borderWidth: this.config.borders.standard,
      }
    )
  }

  /**
   * Render inspection control table
   */
  renderInspectionTable(
    doc: jsPDF,
    examDate: string,
    examType: string,
    status: string,
    nextExam: string,
    startY: number
  ): number {
    return this.renderTable(
      doc,
      [[
        CONTROL_CARD_TEXT.no.headers.examDate,
        CONTROL_CARD_TEXT.no.headers.examType,
        CONTROL_CARD_TEXT.no.headers.status,
        CONTROL_CARD_TEXT.no.headers.nextExam,
      ]],
      [[examDate, examType, status, nextExam]],
      {
        startY,
        columnRatios: [...this.config.table.columnRatios.inspection],
        borderWidth: this.config.borders.thin,
        headerStyle: {
            fontSize: this.config.typography.tableHeader.size, // Match company info table
            cellPadding: 1,
        },
        bodyStyle: {
            fontSize: this.config.typography.body.size, // Match company info table (8)
            cellPadding: this.config.spacing.cellPadding,
        }
      }
    )
  }

  /**
   * Render test/control table
   */
  renderTestControlTable(
    doc: jsPDF,
    testedAccordingTo: string,
    checklist: string,
    startY: number
  ): number {
    return this.renderTable(
      doc,
      [[CONTROL_CARD_TEXT.no.headers.testedAccordingTo, CONTROL_CARD_TEXT.no.headers.checklist]],
      [[testedAccordingTo, checklist]],
      {
        startY,
        columnRatios: [...this.config.table.columnRatios.testControl],
        borderWidth: this.config.borders.thin,
        headerStyle: {
            fontSize: this.config.typography.tableHeader.size, // Match company info table
            cellPadding: 1,
        },
        bodyStyle: {
            fontSize: this.config.typography.body.size, // Match company info table (8)
            cellPadding: this.config.spacing.cellPadding,
        }
      }
    )
  }

  /**
   * Render remarks table
   * @param minHeight - Optional minimum height for the remarks content cell (used to fill space)
   */
  renderRemarksTable(
    doc: jsPDF,
    remarks: string,
    startY: number,
    minHeight?: number
  ): number {
    const tableStartX = this.config.layout.tableStartX
    const tableWidth = this.config.layout.tableWidth
    // Use provided minHeight or fall back to default
    const remarksMinHeight = minHeight ?? this.config.spacing.remarksMinHeight

    autoTable(doc, {
      body: [
        [CONTROL_CARD_TEXT.no.headers.remarks],
        [remarks || ''],
      ],
      startY,
      margin: { left: tableStartX },
      theme: 'grid',
      styles: {
        lineColor: this.config.colors.border,
        lineWidth: this.config.borders.thin,
      },
      bodyStyles: {
        fontSize: this.config.typography.body.size,
        cellPadding: this.config.spacing.cellPadding,
        textColor: this.config.colors.text,
      },
      columnStyles: {
        0: { cellWidth: tableWidth },
      },
      didParseCell: (data: any) => {
        if (data.row.index === 0) {
          data.cell.styles.fillColor = this.config.colors.white
          data.cell.styles.fontStyle = 'bold'
        }
        if (data.row.index === 1) {
          data.cell.styles.minCellHeight = remarksMinHeight
        }
      },
      willDrawCell: (data) => {
        if (data.cell) {
          data.cell.styles.lineWidth = this.config.borders.thin
        }
      },
    })

    return doc.lastAutoTable.finalY
  }

  /**
   * Render regulatory text table
   */
  renderRegulatoryTextTable(
    doc: jsPDF,
    startY: number
  ): number {
    const tableStartX = this.config.layout.tableStartX
    const tableWidth = this.config.layout.tableWidth

    autoTable(doc, {
      body: [[CONTROL_CARD_TEXT.no.regulatory]],
      startY,
      margin: { left: tableStartX },
      theme: 'grid',
      styles: {
        lineColor: this.config.colors.border,
        lineWidth: this.config.borders.thin,
      },
      bodyStyles: {
        fontSize: this.config.typography.footer.size,
        cellPadding: this.config.spacing.cellPadding,
        halign: 'center',
        textColor: this.config.colors.text,
      },
      columnStyles: {
        0: { cellWidth: tableWidth },
      },
      willDrawCell: (data) => {
        if (data.cell) {
          data.cell.styles.lineWidth = this.config.borders.thin
        }
      },
    })

    return doc.lastAutoTable.finalY
  }

  /**
   * Render status codes explanation table
   */
  renderStatusCodesTable(
    doc: jsPDF,
    startY: number
  ): number {
    const tableStartX = this.config.layout.tableStartX
    const tableWidth = this.config.layout.tableWidth

    autoTable(doc, {
      body: [[CONTROL_CARD_TEXT.no.statusCodes]],
      startY,
      margin: { left: tableStartX },
      theme: 'grid',
      styles: {
        lineColor: this.config.colors.border,
        lineWidth: this.config.borders.thin,
      },
      bodyStyles: {
        fontSize: this.config.typography.small.size,
        cellPadding: this.config.spacing.cellPadding,
        halign: 'center',
        textColor: this.config.colors.text,
      },
      columnStyles: {
        0: { cellWidth: tableWidth },
      },
      willDrawCell: (data) => {
        if (data.cell) {
          data.cell.styles.lineWidth = this.config.borders.thin
        }
      },
    })

    return doc.lastAutoTable.finalY
  }

}
